import { create } from 'zustand';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { applyPrefs, readCachedPrefs } from '../lib/applyPrefs';
import { PREFS_SETTING_KEY, parsePreferences, type Preferences } from './schema';

interface UserSettingDto {
  userSettingId?: string;
  userId: string;
  settingKey: string;
  settingValue: string | null;
}

interface PrefsState {
  prefs: Preferences;
  source: 'default' | 'cache' | 'server';
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  userId: string | null;
  set: (partial: Partial<Preferences>) => void;
  hydrateFromServer: (userId: string) => Promise<void>;
  persistNow: () => Promise<void>;
  reset: () => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export const usePrefsStore = create<PrefsState>((set, get) => ({
  prefs: parsePreferences(readCachedPrefs()),
  source: readCachedPrefs() ? 'cache' : 'default',
  dirty: false,
  saving: false,
  lastSavedAt: null,
  userId: null,

  set: (partial) => {
    const prefs = { ...get().prefs, ...partial };
    set({ prefs, dirty: true });
    applyPrefs(prefs);
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().persistNow(), 500);
  },

  hydrateFromServer: async (userId) => {
    set({ userId });
    try {
      const dto = await apiClient.get<UserSettingDto>(ENDPOINTS.USER_SETTING(userId, PREFS_SETTING_KEY), {
        octo: { expectSuccess: false },
      });
      if (dto?.settingValue) {
        const prefs = parsePreferences(JSON.parse(dto.settingValue));
        set({ prefs, source: 'server', dirty: false });
        applyPrefs(prefs);
        return;
      }
    } catch {
      /* sunucu yoksa cache/varsayılan ile devam */
    }
    applyPrefs(get().prefs);
  },

  persistNow: async () => {
    const { userId, prefs, dirty } = get();
    if (!userId || !dirty) return;
    set({ saving: true });
    try {
      await apiClient.post(
        ENDPOINTS.USER_SETTINGS_SAVE,
        { userId, settingKey: PREFS_SETTING_KEY, settingValue: JSON.stringify(prefs) } satisfies UserSettingDto,
        { octo: { expectSuccess: false } },
      );
      set({ dirty: false, lastSavedAt: Date.now(), source: 'server' });
    } catch {
      /* offline: dirty kalır, bir sonraki set'te tekrar denenir */
    } finally {
      set({ saving: false });
    }
  },

  reset: () => {
    const prefs = parsePreferences({});
    set({ prefs, dirty: true });
    applyPrefs(prefs);
    void get().persistNow();
  },
}));

// Sistem teması değişirse (theme=system) yeniden uygula
// matchMedia her ortamda yok (jsdom, eski tarayıcı) — yokluğunda tema sistem değişimini izlemez
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { prefs } = usePrefsStore.getState();
    if (prefs.theme === 'system') applyPrefs(prefs);
  });
  window.addEventListener('online', () => void usePrefsStore.getState().persistNow());
}
